import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Copy, ExternalLink, Printer } from "lucide-react";

export default function QrCodeDialog({ qrToken, equipmentLabel, equipment, open, onOpenChange }) {
  const ref = useRef(null);
  const url = `${window.location.origin}/eq/${qrToken}`;

  const getDataUrl = () => ref.current?.querySelector("canvas")?.toDataURL("image/png") || "";

  const download = () => {
    const dataUrl = getDataUrl();
    if (!dataUrl) return;
    const link = document.createElement("a");
    link.download = `qrcode-${qrToken}.png`;
    link.href = dataUrl;
    link.click();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const printLabel = () => {
    const dataUrl = getDataUrl();
    if (!dataUrl) return;
    const type = equipment?.equipment_type || equipmentLabel || "Equipamento";
    const model = [equipment?.brand, equipment?.model].filter(Boolean).join(" ");
    const local = equipment?.location || "";
    const company = equipment?.contract_company || "";
    const logo = `${window.location.origin}/logo-clima-branca-icon.png`;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const win = window.open("", "_blank", "width=420,height=640");
    if (!win) { toast.error("Permita pop-ups para imprimir a etiqueta."); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiqueta - ${esc(type)}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif}
        body{padding:16px;color:#0f172a}
        .label{width:360px;border:2px solid #0a2a6c;border-radius:14px;overflow:hidden}
        .head{background:linear-gradient(135deg,#0a2a6c,#1e7be0);color:#fff;padding:12px 14px;display:flex;align-items:center;gap:10px}
        .head img{height:34px;width:34px;border-radius:8px;background:#fff}
        .head b{font-size:15px;display:block;line-height:1.1}
        .head span{font-size:9px;letter-spacing:2px;text-transform:uppercase;opacity:.8}
        .body{padding:14px;text-align:center}
        .body h1{font-size:17px;color:#0a2a6c;margin-bottom:2px}
        .body .model{font-size:12px;color:#475569;margin-bottom:8px}
        .meta{font-size:12px;color:#334155;margin:2px 0}
        .qr{margin:12px auto 6px;display:inline-block;padding:8px;border:1px solid #e2e8f0;border-radius:10px}
        .qr img{width:200px;height:200px;display:block}
        .cta{font-size:12px;color:#1e7be0;font-weight:bold;margin-top:6px}
        .foot{font-size:9px;color:#94a3b8;margin-top:6px}
      </style></head>
      <body onload="setTimeout(function(){window.print();},250)">
        <div class="label">
          <div class="head"><img src="${logo}"/><div><b>Clima Branca</b><span>Franquias</span></div></div>
          <div class="body">
            <h1>${esc(type)}</h1>
            ${model ? `<div class="model">${esc(model)}</div>` : ""}
            ${local ? `<div class="meta"><b>Local:</b> ${esc(local)}</div>` : ""}
            ${company ? `<div class="meta">${esc(company)}</div>` : ""}
            <div class="qr"><img src="${dataUrl}"/></div>
            <div class="cta">Aponte a câmera para abrir um chamado</div>
            <div class="foot">${esc(url)}</div>
          </div>
        </div>
      </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="qr-code-dialog">
        <DialogHeader>
          <DialogTitle>QR Code do equipamento</DialogTitle>
          <DialogDescription>{equipmentLabel}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          {qrToken && (
            <div ref={ref} className="p-4 bg-white rounded-xl ring-1 ring-slate-200" data-testid="qr-canvas-wrap">
              <QRCodeCanvas value={url} size={220} level="M" includeMargin
                imageSettings={{ src: "/logo-clima-branca-icon.png", height: 40, width: 40, excavate: true }} />
            </div>
          )}
          <p className="text-xs text-slate-400 break-all text-center">{url}</p>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={printLabel} data-testid="qr-print-button">
            <Printer className="h-4 w-4" /> Imprimir etiqueta
          </Button>
          <div className="grid grid-cols-3 gap-2 w-full">
            <Button variant="outline" className="gap-1.5" onClick={download} data-testid="qr-download-button">
              <Download className="h-4 w-4" /> PNG
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={copy} data-testid="qr-copy-button">
              <Copy className="h-4 w-4" /> Link
            </Button>
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="qr-open-link">
              <Button variant="outline" className="w-full gap-1.5"><ExternalLink className="h-4 w-4" /> Abrir</Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
